import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';

export type CareGuideTip = {
  heading: string;
  content: string;
};

export type CareGuideSendChannel = 'email' | 'sms' | 'both';
export type CareGuideScopeType = 'all_devices' | 'categories' | 'makes';

export type CareGuideTemplate = {
  id: string;
  tenantId: string;
  storeId?: string | null;
  repairType: string;
  title: string;
  emailSubject?: string | null;
  intro?: string | null;
  tips: CareGuideTip[];
  warrantyReminder?: string | null;
  contactInfoShown: boolean;
  sendChannel: CareGuideSendChannel;
  sortOrder: number;
  scopeType: CareGuideScopeType;
  scopeCategories: string[];
  scopeMakeIds: string[];
  scopeExcludedModelIds: string[];
  active: boolean;
  sendCount: number;
  lastSentAt?: string | null;
  createdAt: string;
  updatedAt?: string;
};

export type GeneratedCareGuide = {
  title: string;
  emailSubject?: string;
  intro: string;
  tips: CareGuideTip[];
  warrantyReminder: string | null;
  repairTypes: string[];
  html: string;
  text: string;
  device: string;
  customerName: string;
  templateIds?: string[];
  unresolvedTokens?: string[];
  sampleMode?: 'ticket' | 'sample';
  mergeFields?: Record<string, string>;
};

export type CareGuideSendVia = 'email' | 'sms' | 'both';

export type CareGuideSendResult = {
  sent: boolean;
  email?: string;
  sms?: string;
};

export type CareGuideTemplateInput = {
  repairType: string;
  title: string;
  emailSubject?: string | null;
  intro?: string | null;
  tips?: CareGuideTip[];
  warrantyReminder?: string | null;
  contactInfoShown?: boolean;
  sendChannel?: CareGuideSendChannel;
  sortOrder?: number;
  scopeType?: CareGuideScopeType;
  scopeCategories?: string[];
  scopeMakeIds?: string[];
  active?: boolean;
};

export type CareGuideTemplateUpdate = Partial<CareGuideTemplateInput>;

export function listCareGuideTemplates(params?: { active?: 'true' | 'false' | 'all' }) {
  return apiGet<CareGuideTemplate[]>('/api/v1/care-guides', { params }).then((r) => r.data);
}

export function getCareGuideTemplate(repairType: string) {
  return apiGet<CareGuideTemplate>(`/api/v1/care-guides/${encodeURIComponent(repairType)}`).then(
    (r) => r.data,
  );
}

export function createCareGuideTemplate(body: CareGuideTemplateInput) {
  return apiPost<CareGuideTemplate, CareGuideTemplateInput>('/api/v1/care-guides', body).then(
    (r) => r.data,
  );
}

export function updateCareGuideTemplateById(id: string, body: CareGuideTemplateUpdate) {
  return apiPatch<CareGuideTemplate, CareGuideTemplateUpdate>(
    `/api/v1/care-guides/by-id/${id}`,
    body,
  ).then((r) => r.data);
}

/** @deprecated Prefer updateCareGuideTemplateById */
export function updateCareGuideTemplate(repairType: string, body: CareGuideTemplateUpdate) {
  return apiPatch<CareGuideTemplate, CareGuideTemplateUpdate>(
    `/api/v1/care-guides/${encodeURIComponent(repairType)}`,
    body,
  ).then((r) => r.data);
}

export function deleteCareGuideTemplate(id: string) {
  return apiDelete<void>(`/api/v1/care-guides/by-id/${id}`);
}

export function previewCareGuideTemplate(body: {
  ticketId?: string;
  draft?: CareGuideTemplateUpdate;
  templateId?: string;
}) {
  if (body.templateId) {
    const path = `/api/v1/care-guides/by-id/${body.templateId}/preview`;
    const payload: { ticketId?: string; draft?: CareGuideTemplateUpdate } = {};
    if (body.ticketId) payload.ticketId = body.ticketId;
    if (body.draft) payload.draft = body.draft;
    return apiPost<GeneratedCareGuide, typeof payload>(path, payload).then((r) => r.data);
  }
  const payload: { ticketId?: string; draft?: CareGuideTemplateUpdate } = {};
  if (body.ticketId) payload.ticketId = body.ticketId;
  if (body.draft) payload.draft = body.draft;
  return apiPost<GeneratedCareGuide, typeof payload>('/api/v1/care-guides/preview', payload).then(
    (r) => r.data,
  );
}

export function generateCareGuide(ticketId: string) {
  return apiPost<GeneratedCareGuide>(`/api/v1/care-guides/generate/${ticketId}`).then((r) => r.data);
}

export function sendCareGuide(
  ticketId: string,
  body?: { via?: CareGuideSendVia; templateId?: string },
) {
  return apiPost<CareGuideSendResult, { via?: CareGuideSendVia; templateId?: string }>(
    `/api/v1/care-guides/send/${ticketId}`,
    body,
  ).then((r) => r.data);
}
